import type { SftpSalesLocationMappingError } from '@gcv/shared';
import { HttpStatusCode } from '@gcv/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GcvAdminApi } from 'api';
import { ApiError } from 'api/api-util/api-error';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { DISPENSARY_KEY, SFTP_SALES_PROVIDERS_KEY } from 'tanstack-hooks/keys';

const getLocationMappingErrors = (error: unknown): SftpSalesLocationMappingError[] =>
  error instanceof ApiError &&
  error.response?.status === HttpStatusCode.BAD_REQUEST &&
  Array.isArray(error.response.data)
    ? error.response.data
    : [];

export function useCreateSftpSalesProvider() {
  const gcvAdminApi = new GcvAdminApi();
  const queryClient = useQueryClient();

  const {
    mutateAsync: createSftpSalesProvider,
    data: createdSftpSalesProvider,
    error,
    isPending: isCreatingSftpSalesProvider
  } = useMutation({
    mutationKey: [SFTP_SALES_PROVIDERS_KEY, 'createProvider'],
    // the UI never overwrites a CRB's POS config; confirmPosOverwrite is for scripts only
    mutationFn: ({
      name,
      contactEmail,
      mappings
    }: {
      name: string;
      contactEmail?: string;
      mappings: { locationId: string; crbId: string }[];
    }) =>
      gcvAdminApi.createSftpSalesProvider({
        name,
        contactEmail,
        mappings: mappings.map((mapping) => ({ ...mapping, confirmPosOverwrite: false }))
      }),
    onSuccess: (_, { mappings }) => {
      queryClient.invalidateQueries({ queryKey: [SFTP_SALES_PROVIDERS_KEY] });
      mappings.forEach(({ crbId }) => queryClient.invalidateQueries({ queryKey: [DISPENSARY_KEY, crbId] }));
    },
    onError: (error) => {
      getSnackbarStore().showErrorSnackbarMessage(
        getLocationMappingErrors(error).length
          ? 'Some location mappings are invalid. Fix the rows below and try again.'
          : 'There was an issue creating the provider. Please try again.'
      );
    },
    meta: {
      snackbarSuccessMessage: 'Provider created successfully.'
    }
  });

  return {
    createSftpSalesProvider,
    createdSftpSalesProvider,
    sftpSalesLocationMappingErrors: getLocationMappingErrors(error),
    isCreatingSftpSalesProvider
  };
}
